import { Typography, Stack, Button, Divider, Alert, Card, Input, Box, IconButton } from '@mui/joy';
import { useEffect, useMemo, useState } from 'react';

import EditUnderlyingModal from './EditUnderlyingModal';
import {
  type IUnderlyingAssetsOverridesQuery,
  useDeleteUnderlyingAssetOverrideMutation,
  useUnderlyingAssetsOverridesQuery,
} from '../../../../../generated/graphql';
import { useDefaultErrorHandling } from 'components/technical/UseDefaultErrorHandling';
import AssetsChipList from '../../AssetsChipList';
import AssetLabel from '../../AssetLabel';
import { Edit, Search, TrashBin } from 'components/technical/icons';
import type { NotVerifiedAssetWithId } from '../../AssetService';
import { IconVariant } from '../../cryptocurrencies/CryptocurrenciesData';
import { useFeedback } from 'components/technical/Feedback/UseFeedback';

export type UnderlyingAssetKey = { id: string; symbol: string };

export type GroupedUnderlyingAssetMap = Map<NotVerifiedAssetWithId, NotVerifiedAssetWithId[]>;

function createUnderlyingAssetMap(
  data: IUnderlyingAssetsOverridesQuery['assets']['underlyingAssetOverrides']
): GroupedUnderlyingAssetMap {
  const map: GroupedUnderlyingAssetMap = new Map();

  for (const item of data) {
    const key = item.underlyingAsset;

    if (!map.has(key)) {
      map.set(key, []);
    }

    map.get(key)!.push(item.asset);
  }

  return map;
}

const EmptyState = () => (
  <Stack direction="row" justifyContent="center" py={2} width="100%">
    <Alert color="neutral" variant="outlined">
      No existing custom links
    </Alert>
  </Stack>
);

export const UnderlyingAssets = () => {
  const [selected, setSelected] = useState<{
    underlyingAsset: NotVerifiedAssetWithId;
    assets: NotVerifiedAssetWithId[];
  } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data, Fallback, loaded, refetch } = useDefaultErrorHandling(
    useUnderlyingAssetsOverridesQuery({
      fetchPolicy: 'network-only',
    })
  );

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(timeout);
  }, [search]);

  const rawOverrides = data?.assets.underlyingAssetOverrides ?? [];

  const underlyingAssetMap = useMemo(() => createUnderlyingAssetMap(rawOverrides), [rawOverrides]);
  const { showSuccessMessage } = useFeedback();

  const [deleteOverride] = useDeleteUnderlyingAssetOverrideMutation();

  const filteredMap = useMemo(() => {
    const entries = Array.from(underlyingAssetMap.entries())
      .filter(([asset]) => asset.symbol?.toLowerCase().includes(debouncedSearch.toLowerCase()))
      .sort(([a], [b]) => a.symbol?.localeCompare(b.symbol ?? '') ?? 0);

    return new Map(entries);
  }, [underlyingAssetMap, debouncedSearch]);

  const handleCloseModal = () => {
    setSelected(null);
    setShowCreateModal(false);
  };

  const handleOnSuccess = () => {
    handleCloseModal();
    refetch();
  };

  if (!loaded) return <Fallback />;

  // Delete underlying asset and all linked assets
  const handleDeleteUnderlyingAsset = async ({
    underlyingAsset,
    assets,
  }: { underlyingAsset: NotVerifiedAssetWithId; assets: NotVerifiedAssetWithId[] }) => {
    await deleteOverride({
      variables: {
        input: {
          assetIds: assets.map((a) => a.id),
        },
      },
    });
    showSuccessMessage('Underlying asset deleted');
    refetch();
    underlyingAssetMap.delete(underlyingAsset);
  };

  return (
    <>
      <Stack direction="column" spacing={1} p={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" alignSelf="flex-end">
          <Stack direction="row" spacing={1}>
            <Input
              startDecorator={<Search color="neutral" size="sm" />}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button variant="outlined" size="sm" onClick={() => setShowCreateModal(true)}>
              Create
            </Button>
          </Stack>
        </Stack>
        <Divider />
        {filteredMap.size > 0 ? (
          <Stack direction="column" spacing={1} my={2}>
            <Typography level="h3">Existing underlying asset Links</Typography>
            {Array.from(filteredMap.entries()).map(([underlyingAsset, items]) => (
              <Card key={underlyingAsset.id}>
                <Stack direction="row" width="100%" justifyContent="space-between" spacing={4}>
                  <Stack direction="column" spacing={1} justifyContent="flex-start" alignItems="baseline">
                    <Typography level="title-sm">Underlying Asset</Typography>
                    <AssetLabel asset={underlyingAsset} wrap={false} size={IconVariant.MEDIUM} withSymbol plain />
                  </Stack>
                  <Stack direction="column" flex={1} justifyContent="flex-end" spacing={1}>
                    <Typography level="title-sm">Linked Assets</Typography>
                    <AssetsChipList assets={items} height="80px" renderItem={(asset) => <AssetLabel asset={asset} />} />
                  </Stack>
                  <Box sx={{ display: 'absolute', right: 0, top: 0 }}>
                    <IconButton
                      variant="plain"
                      color="neutral"
                      size="sm"
                      onClick={() => {
                        setShowCreateModal(true);
                        setSelected({ underlyingAsset, assets: items });
                      }}
                    >
                      <Edit color="neutral" fontSize="sm" />
                    </IconButton>
                    <IconButton
                      variant="plain"
                      color="neutral"
                      size="sm"
                      onClick={() => handleDeleteUnderlyingAsset({ underlyingAsset, assets: items })}
                    >
                      <TrashBin color="neutral" fontSize="sm" />
                    </IconButton>
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </Stack>
      {showCreateModal && (
        <EditUnderlyingModal selected={selected} onClose={handleCloseModal} onSuccess={handleOnSuccess} />
      )}
    </>
  );
};
